import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import HomeVisitClinicalRecordForm from "./HomeVisitClinicalRecordForm";
import { createHomeVisitClinicalRecord } from "../../store/slices/homeVisitClinicalRecordSlice";
import { fetchPatient } from "../../store/slices/patientsSlice";

const AddHomeVisitClinicalRecord = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const [formData, setFormData] = useState({
    patient_id: patientId || "",
    case_id: caseId || "",
    
    // Visit Details
    visit_number: "",
    date_of_visit: new Date().toISOString().split("T")[0],
    time_in: "",
    time_out: "",
    duration: "",
    location_address: "",
    type_of_visit: "",

    // Team
    psychiatrist_name: "",
    rehab_practitioner_name: "",
    rehab_assistant_name: "",
    driver_name: "",
    team_gender: "",
    family_member_name: "",
    family_member_relation: "",
    family_member_contact: "",

    // Safety
    safety_environment_safe: false,
    safety_no_weapons: false,
    safety_no_intoxicated: false,
    safety_client_stable: false,
    safety_family_cooperative: false,
    safety_unsafe_action_description: "",

    // Clinical Status
    clinical_appearance_hygiene: "",
    clinical_behavior: "",
    clinical_speech: "",
    clinical_mood_affect: "",
    clinical_thought_content: "",
    clinical_perception: "",
    clinical_insight: "",
    clinical_judgment: "",
    clinical_suicidal_ideation: false,
    clinical_homicidal_ideation: false,
    clinical_impulsivity: false,
    clinical_risk_level: "",
    clinical_risk_action_taken: "",

    // Medication Review
    med_currently_on: false,
    med_adherence: "",
    med_side_effects: "",
    med_changes: "",
    med_changes_details: "",
    med_prescribed_by: "",

    // Psychoedu
    psychoedu_illness_understanding: false,
    psychoedu_medication_importance: false,
    psychoedu_relapse_signs: false,
    psychoedu_behavior_management: false,
    psychoedu_communication_strategies: false,
    psychoedu_crisis_handling: false,
    psychoedu_rehab_readiness: false,
    psychoedu_residential_vs_drp: false,
    psychoedu_family_response: "",
    psychoedu_notes: "",

    // Engagement
    engagement_participation: "",
    engagement_issues_discussed: "",
    engagement_coping_style: "",
    engagement_motivation: "",

    // Goals
    goals_short_term_1: "",
    goals_short_term_2: "",
    goals_suitability: "",

    // Plan
    plan_decision: "",
    plan_next_visit_date: "",

    // Incident
    incident_aggression_disruption: false,
    incident_description: "",
    incident_action_taken: "",

    // Impression
    impression_summary: "",
    impression_progress: "",

    // Audit
    audit_completed_within_1hour: false,
    audit_documentation_same_day: false,
    audit_medication_chart_updated: false,
    audit_risk_reassessment_completed: false,

    // Signatures
    signature_psychiatrist: "",
    signature_psychiatrist_date: "",
    signature_rehab_practitioner: "",
    signature_family_member: "",

    // Finance fields
    amount: "",
    payment_status: "",
    reference_no: "",
    payment_date: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatient(patientId))
        .unwrap()
        .then((patient) => {
          if (patient) {
            setFormData((prev) => ({
              ...prev,
              location_address: patient.address || "",
            }));
          }
        })
        .catch((err) => console.error("Failed to fetch patient details:", err));
    }
  }, [dispatch, patientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.visit_number) newErrors.visit_number = "Visit Number is required";
    if (!formData.date_of_visit) newErrors.date_of_visit = "Date of Visit is required";
    if (!formData.type_of_visit) newErrors.type_of_visit = "Type of Visit is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill all required fields in the form (Visit Number, Date, and Type).",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      const payload = {
        ...formData,
        // Make sure date values are null instead of empty string if not filled
        plan_next_visit_date: formData.plan_next_visit_date || null,
        signature_psychiatrist_date: formData.signature_psychiatrist_date || null,
      };

      const res = await dispatch(createHomeVisitClinicalRecord(payload)).unwrap();

      Swal.fire({
        title: "Success!",
        text: "Home Visit Clinical Record created successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      navigate(`/home-visitation/clinical-record/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
    } catch (err) {
      let errorMessage = "Failed to create Home Visit Clinical record";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (typeof err === "object" && err !== null) {
        errorMessage = Object.values(err).flat().join("\n");
      }
      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />
        <div className="card shadow-sm border-0">
          <div className="card-header border-bottom py-3 d-flex align-items-center justify-content-between">
            <h5 className="card-title mb-0">Add Home Visit Clinical Record Form</h5>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <HomeVisitClinicalRecordForm
              formData={formData}
              setFormData={setFormData}
              handleChange={handleChange}
              errors={errors}
            />
            <div className="card-footer border-top bg-white py-3 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Save Clinical Record
              </button>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddHomeVisitClinicalRecord;
